import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatSlotTime, timezoneAbbreviation } from "../../domain/formatting";
import { slotMatchingTyped } from "../../domain/slotSelection";

interface ComboboxSlot {
    /** UTC ISO-8601 instant. */
    start: string;
    duration_minutes: number;
}

interface TimeSlotComboboxProps {
    slots: readonly ComboboxSlot[];
    /** The therapist's timezone; every time shown is in it. */
    timezone: string;
    selected: string | null;
    onSelect: (start: string) => void;
    disabled?: boolean;
}

/**
 * A start-time field in the manner of a calendar app: type a time or open the
 * list, and the session's end sits beside it.
 *
 * Starts come every quarter hour, so a day is dozens of them; a list that only
 * opens when asked keeps the form short, and typing "1615" is quicker than
 * scanning for it. Only free starts are listed, and a typed time that is not
 * one of them is refused rather than rounded — rounding would book a time the
 * therapist never wrote.
 */
export default function TimeSlotCombobox({
    slots,
    timezone,
    selected,
    onSelect,
    disabled = false,
}: TimeSlotComboboxProps) {
    const { t } = useTranslation();
    const listId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [unavailable, setUnavailable] = useState<string | null>(null);

    const selectedSlot = slots.find((slot) => slot.start === selected) ?? null;
    const selectedIndex = selectedSlot ? slots.indexOf(selectedSlot) : -1;
    const shown = (slot: ComboboxSlot) => formatSlotTime(slot.start, timezone);

    // Keep the highlighted option in view as the keyboard or typing moves it.
    useEffect(() => {
        if (!open || activeIndex < 0) return;
        listRef.current
            ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
            ?.scrollIntoView?.({ block: "nearest" });
    }, [open, activeIndex]);

    const openList = () => {
        if (disabled || slots.length === 0) return;
        setOpen(true);
        setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    };

    const close = () => {
        setOpen(false);
        setQuery(null);
    };

    const choose = (index: number) => {
        const slot = slots[index];
        if (!slot) return;
        onSelect(slot.start);
        setUnavailable(null);
        close();
    };

    /** Settle whatever was typed: an exact free start, or a refusal. */
    const commitTyped = () => {
        if (query === null || query.trim() === "") {
            close();
            return;
        }
        const match = slotMatchingTyped(slots, timezone, query);
        if (match.exact !== null) {
            choose(match.exact);
            return;
        }
        setUnavailable(query.trim());
        close();
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!open) {
                openList();
                return;
            }
            const step = event.key === "ArrowDown" ? 1 : -1;
            setActiveIndex((index) =>
                Math.min(slots.length - 1, Math.max(0, index + step))
            );
            return;
        }
        if (event.key === "Enter") {
            if (!open) return;
            event.preventDefault();
            if (query !== null && query.trim() !== "") commitTyped();
            else choose(activeIndex);
            return;
        }
        if (event.key === "Escape" && open) {
            event.preventDefault();
            event.stopPropagation();
            close();
        }
    };

    const end = selectedSlot
        ? formatSlotTime(
              new Date(
                  Date.parse(selectedSlot.start) +
                      selectedSlot.duration_minutes * 60_000
              ).toISOString(),
              timezone
          )
        : null;
    const zone = timezoneAbbreviation(
        selectedSlot?.start ?? slots[0]?.start ?? new Date().toISOString(),
        timezone
    );

    return (
        <div className="flex flex-col gap-1">
            <div className="relative flex flex-wrap items-center gap-x-3 gap-y-1">
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    inputMode="numeric"
                    autoComplete="off"
                    aria-label={t("scheduler.schedule.time")}
                    aria-expanded={open}
                    aria-controls={listId}
                    aria-autocomplete="list"
                    aria-activedescendant={
                        open && activeIndex >= 0
                            ? `${listId}-${activeIndex}`
                            : undefined
                    }
                    aria-invalid={unavailable !== null}
                    placeholder={t("scheduler.schedule.timePlaceholder")}
                    value={query ?? (selectedSlot ? shown(selectedSlot) : "")}
                    disabled={disabled || slots.length === 0}
                    onFocus={openList}
                    onClick={openList}
                    onChange={(event) => {
                        const text = event.target.value;
                        setQuery(text);
                        setUnavailable(null);
                        setOpen(true);
                        const match = slotMatchingTyped(slots, timezone, text);
                        if (match.nearest !== null) setActiveIndex(match.nearest);
                    }}
                    onBlur={commitTyped}
                    onKeyDown={onKeyDown}
                    className="w-28 cursor-pointer rounded-lg border border-gray-300 px-3 py-2 text-sm tabular-nums focus:cursor-text focus:border-brand-500 aria-[invalid=true]:border-red-500 dark:border-gray-700 dark:bg-gray-900"
                />

                {end && (
                    <span className="text-sm tabular-nums text-gray-600 dark:text-gray-300">
                        {t("scheduler.schedule.endsAt", {
                            time: end,
                            minutes: selectedSlot?.duration_minutes,
                        })}
                    </span>
                )}
                {zone && (
                    <span className="text-xs text-content-tertiary">{zone}</span>
                )}

                {open && (
                    <ul
                        ref={listRef}
                        id={listId}
                        role="listbox"
                        aria-label={t("scheduler.schedule.time")}
                        className="absolute left-0 top-full z-30 mt-1 max-h-60 w-40 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                    >
                        {slots.map((slot, index) => {
                            const isSelected = slot.start === selected;
                            const isActive = index === activeIndex;
                            return (
                                <li
                                    key={slot.start}
                                    id={`${listId}-${index}`}
                                    role="option"
                                    aria-selected={isSelected}
                                    data-index={index}
                                    // Keep focus in the field, so its blur does
                                    // not settle a half-typed time first.
                                    onMouseDown={(event) => event.preventDefault()}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onClick={() => choose(index)}
                                    className={`cursor-pointer px-3 py-1.5 text-sm tabular-nums ${
                                        isActive ? "bg-brand-50 dark:bg-white/10" : ""
                                    } ${
                                        isSelected
                                            ? "font-semibold text-brand-700 dark:text-brand-300"
                                            : "text-gray-800 dark:text-gray-100"
                                    }`}
                                >
                                    {shown(slot)}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {unavailable !== null && (
                <p role="alert" className="text-xs text-red-600 dark:text-red-400">
                    {t("scheduler.schedule.timeUnavailable", { time: unavailable })}
                </p>
            )}
        </div>
    );
}
